/** Compiled Ace fixes — copy or open the original task with the prompt prefilled. */

import { useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCopy,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import { useOsNavigate } from "@/components/ui/os-layout";
import { compileArtefactFixPrompt } from "./artefactCompiledFixes";
import {
  missionControlTaskPath,
  stashTaskThreadDraft,
} from "./taskThreadDraftStorage";
import type { AceArtefactRating, AgentRun, ArtefactReviewItem, PhaseCriteria } from "./types";

interface CompiledArtefactFixesPanelProps {
  run: AgentRun;
  artefact: ArtefactReviewItem;
  criteria: PhaseCriteria;
  aceArtefact: AceArtefactRating;
}

export function CompiledArtefactFixesPanel({
  run,
  artefact,
  criteria,
  aceArtefact,
}: CompiledArtefactFixesPanelProps) {
  const osNavigate = useOsNavigate();
  const [copied, setCopied] = useState(false);
  const prompt = compileArtefactFixPrompt(artefact.title, criteria, aceArtefact);
  const taskId = run.task_id ?? null;
  const interactionId = run.interaction_id ?? run.run_id;

  const handleCopy = useCallback(async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [prompt]);

  const handleOpenTask = useCallback(() => {
    if (!prompt || !taskId) return;
    stashTaskThreadDraft({ taskId, message: prompt });
    osNavigate(missionControlTaskPath(taskId, interactionId), "Mission Control");
  }, [prompt, taskId, interactionId, osNavigate]);

  if (!prompt) return null;

  return (
    <div
      className="space-y-3 rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5"
      data-testid="compiled-artefact-fixes"
    >
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
          Prompt for the original task
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Send this to the agent on the task that produced this document.
        </p>
      </div>
      <textarea
        readOnly
        value={prompt}
        rows={Math.min(8, Math.max(4, prompt.split("\n").length + 1))}
        className="w-full resize-y rounded-lg border-0 bg-slate-900/60 px-3 py-2 text-xs leading-relaxed text-slate-200 ring-1 ring-white/10"
      />
      <div className="flex flex-wrap gap-2">
        <Button plain onClick={() => void handleCopy()} className="!text-slate-300 hover:!text-white">
          <FontAwesomeIcon icon={faCopy} />
          {copied ? "Copied" : "Copy prompt"}
        </Button>
        {taskId ? (
          <Button color="indigo" onClick={handleOpenTask}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            Open original task
          </Button>
        ) : (
          <p className="text-xs text-slate-500 self-center">
            Task link unavailable for this sample run.
          </p>
        )}
      </div>
    </div>
  );
}
